// ============================================================================
// PQ Chat — data layer (Phase A: chat in context on the estimate builder)
// ----------------------------------------------------------------------------
// Conversations + messages live in pq_chat_conversation / pq_chat_message
// (migration 050, tenant RLS). Sending a message invokes the `pq-chat` edge
// function, which assembles the context server-side under the caller's JWT
// (estimate row, walkthrough findings, company profile) and persists both
// turns. Reads here go straight through RLS.
// ============================================================================
import { supabase } from '@/lib/supabaseClient';

/**
 * Send a message to PQ. Pass conversationId to continue a thread, or omit it
 * (with estimateId OR jobId) to start one — the reply includes the
 * conversationId. Resolves { conversationId, reply, proposals, usage }.
 */
export const sendPqChatMessage = async ({ conversationId, estimateId, jobId, message }) => {
  const { data, error } = await supabase.functions.invoke('pq-chat', {
    body: { conversationId, estimateId, jobId, message },
  });
  if (error) throw new Error(await extractPqChatErrorCode(error));
  if (data && data.ok === false) {
    throw new Error(data.error || 'PQ chat failed.');
  }
  return data;
};

/**
 * A non-2xx response from a Supabase edge function surfaces here as a
 * FunctionsHttpError whose `.message` is ALWAYS the generic
 * "Edge Function returned a non-2xx status code" — the actual JSON body
 * pq-chat sent back (e.g. { ok:false, error:"message_too_long" }) only
 * lives on `error.context` (the raw Response), which the SDK never parses
 * for you. Left unwrapped, every rejection — a too-long paste, the daily
 * cap, a full conversation — showed that same useless generic string
 * instead of pqChatErrorMessage's actual guidance (live-fire 2026-09-12:
 * John's big copy/paste got rejected but the toast gave him nothing to
 * act on). Best-effort: falls back to the original message if the
 * context isn't a Response we can read.
 */
const extractPqChatErrorCode = async (error) => {
  try {
    const body = await error?.context?.json?.();
    if (body?.error) return body.error;
  } catch {
    /* context wasn't JSON (or wasn't readable) — fall through */
  }
  return error?.message || 'PQ chat failed.';
};

/**
 * Most recent conversation for a context (reopens the last thread).
 * contextType: 'estimate' | 'job'.
 */
export const fetchLatestConversation = async (contextType, contextId) => {
  if (!contextType || !contextId) return null;
  const { data, error } = await supabase
    .from('pq_chat_conversation')
    .select('id,title,created_at')
    .eq('context_type', contextType)
    .eq('context_id', contextId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data || null;
};

/** Full message history for a conversation, oldest first. */
export const fetchMessages = async (conversationId) => {
  if (!conversationId) return [];
  const { data, error } = await supabase
    .from('pq_chat_message')
    .select('id,role,content,proposals,created_at')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
};

/** Delete a conversation (messages cascade). */
export const deleteConversation = async (conversationId) => {
  const { error } = await supabase
    .from('pq_chat_conversation')
    .delete()
    .eq('id', conversationId);
  if (error) throw error;
  return true;
};

/**
 * Snap-and-ask: attach a photo from the chat. The photo lands in the
 * estimate's walkthrough (created on demand) under a "Chat uploads" area, so
 * it rides the exact same rails as capture-page photos — PQ's
 * view_walkthrough_photos tool, Vision analysis, and Findings Review all see
 * it. Returns { areaName } for the caller to reference in the message.
 */
export const attachPhotoForChat = async (estimateId, file) => {
  if (!estimateId) throw new Error('estimate_required');
  if (!file) throw new Error('file_required');
  const {
    fetchLatestWalkthroughForEstimate,
    startWalkthrough,
    listAreas,
    addArea,
    uploadAreaPhoto,
  } = await import('@/lib/pqvData');

  let walkthrough = await fetchLatestWalkthroughForEstimate(estimateId);
  if (!walkthrough) walkthrough = await startWalkthrough(estimateId);

  const areaName = 'Chat uploads';
  const areas = await listAreas(walkthrough.id);
  let area = (areas || []).find(
    (a) => String(a?.name || '').trim().toLowerCase() === areaName.toLowerCase()
  );
  if (!area) {
    const maxSeq = (areas || []).reduce(
      (m, a) => Math.max(m, Number(a?.sequence) || 0),
      0
    );
    area = await addArea({
      walkthroughId: walkthrough.id,
      name: areaName,
      sequence: maxSeq + 1,
    });
  }

  // sequence is an int4 column — Date.now() overflows it. Seconds since 2020
  // keeps chat attaches ordered after capture-page photos and int-safe.
  await uploadAreaPhoto({
    file,
    areaId: area.id,
    walkthroughId: walkthrough.id,
    sequence: Math.floor((Date.now() - 1577836800000) / 1000),
  });
  return { areaName };
};

/**
 * Phase 4 — supplier RFQ round-trip: upload a priced sheet the supplier sent
 * back. Lands in the private `pq-exports` bucket (tenant-first path, 053/054
 * policies already allow client insert + xlsx mime) under
 * `<tenant>/rfq-returns/<estimateId>/<ts>-<name>.xlsx`, where the pq-chat
 * `import_supplier_quote` tool finds it — the server parses the actual file,
 * so prices never round-trip through the model. Returns { filename }.
 */
const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const uploadSupplierSheetForChat = async (estimateId, file) => {
  if (!estimateId) throw new Error('estimate_required');
  if (!file) throw new Error('file_required');
  if (file.size > 5 * 1024 * 1024) throw new Error('Sheet is over 5 MB.');

  const { data: userRes } = await supabase.auth.getUser();
  const uid = userRes?.user?.id;
  if (!uid) throw new Error('not_signed_in');
  const { data: prof } = await supabase
    .from('profiles')
    .select('contractor_id')
    .eq('id', uid)
    .single();
  const tenant = prof?.contractor_id;
  if (!tenant) throw new Error('no_tenant');

  const safeName =
    String(file.name || 'priced-sheet.xlsx')
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'priced-sheet.xlsx';
  const path = `${tenant}/rfq-returns/${estimateId}/${Date.now()}-${safeName}`;
  const { error } = await supabase.storage
    .from('pq-exports')
    .upload(path, file, { contentType: XLSX_MIME, upsert: false });
  if (error) throw error;
  return { filename: safeName };
};

/**
 * Upload a PDF (supplier quote, invoice, receipt, spec sheet) for PQ to read.
 * Lands in pq-exports/<tenant>/chat-docs/<estimateId>/<ts>-<name>.pdf, where
 * pq-chat's read_attached_document picks up the newest one. 10 MB cap —
 * matches the bucket limit (migration 086) and the server's own check.
 */
export const PDF_MAX_BYTES = 10 * 1024 * 1024;

export const uploadDocumentForChat = async (estimateId, file) => {
  if (!estimateId) throw new Error('estimate_required');
  if (!file) throw new Error('file_required');
  if (file.size > PDF_MAX_BYTES) {
    throw new Error('That PDF is over 10 MB — send just the pages that matter.');
  }

  const { data: userRes } = await supabase.auth.getUser();
  const uid = userRes?.user?.id;
  if (!uid) throw new Error('not_signed_in');
  const { data: prof } = await supabase
    .from('profiles')
    .select('contractor_id')
    .eq('id', uid)
    .single();
  const tenant = prof?.contractor_id;
  if (!tenant) throw new Error('no_tenant');

  const base =
    String(file.name || 'document.pdf')
      .toLowerCase()
      .replace(/\.pdf$/i, '')
      .replace(/[^a-z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'document';
  const safeName = `${base}.pdf`;
  const path = `${tenant}/chat-docs/${estimateId}/${Date.now()}-${safeName}`;
  const { error } = await supabase.storage
    .from('pq-exports')
    .upload(path, file, { contentType: 'application/pdf', upsert: false });
  if (error) throw error;
  return { filename: safeName };
};

/**
 * PQ decision memory (migration 088): the facts the contractor settled, with
 * his own words and the date. Company-wide ones plus this record's. Returns
 * { available: false } until the migration is applied, so the panel hides
 * instead of erroring.
 */
export const fetchPqMemories = async ({ estimateId = null, jobId = null } = {}) => {
  const recordId = estimateId || jobId;
  let q = supabase
    .from('pq_memory')
    .select('id,scope,scope_id,fact,said,created_at')
    .is('forgotten_at', null)
    .order('created_at', { ascending: false })
    .limit(80);
  q = recordId ? q.or(`scope.eq.company,scope_id.eq.${recordId}`) : q.eq('scope', 'company');
  const { data, error } = await q;
  if (error) {
    const missing =
      error.code === '42P01' || /does not exist|could not find the/i.test(error.message || '');
    if (missing) return { available: false, rows: [] };
    throw error;
  }
  return { available: true, rows: data || [] };
};

/** Forget one remembered decision (soft delete — history stays auditable). */
export const forgetPqMemory = async (id) => {
  const { error } = await supabase
    .from('pq_memory')
    .update({ forgotten_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
};

/** Map the edge function's error codes to contractor-readable messages. */
export const pqChatErrorMessage = (err) => {
  const code = String(err?.message || '');
  switch (code) {
    case 'daily_limit_reached':
      return "PQ has hit today's usage limit — try again tomorrow.";
    case 'conversation_full':
      return 'This conversation is full — start a new one to keep going.';
    case 'message_too_long':
      return 'That message is too long — trim it down and resend.';
    default:
      return code || 'PQ chat failed.';
  }
};
